import React, { useEffect, useState, useRef, ChangeEvent, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import MoreVert from '@material-ui/icons/MoreVert';
import Alert from '@material-ui/lab/Alert';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import {
  createSerialNumber,
  getSerialNumbers,
  fetchSerialNumbers,
  getSerialNumbersLoaders,
  getSerialNumbersApiErrors,
  getSerialNumbersPagination,
  getSelectedSerialNumbers,
  deleteSerialNumber,
  getSerialNumberGroups,
  resetSerialNumbersList,
  selectSerialNumber,
  SerialNumberGroup,
  resetSelectedSerialNumber
} from 'features/SerialNumbers/serialNumbers';
import { ConfigureLabelForPrint } from 'features/PrintServices/ConfigureLabelForPrintDialog';
import { SerialNumberSnapshots } from 'features/SerialNumbers/SerialNumberSnapshots';
import { IssueSerialNumberCountDialog } from 'features/SerialNumbers/IssueSerialNumberCountDialog';
import { ConfirmDeleteSerialNumber } from 'features/SerialNumbers/ConfirmDeleteSerialNumber';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { getSites, getSitesLoaders } from 'features/Sites/sites';
import { Row } from 'components/UI/Row';
import { SerialNumberRow } from 'features/SerialNumbers/SerialNumberRow';
import { GroupsLoader } from 'features/SerialNumbers/Loaders/GroupsLoader';
import { Column } from 'components/UI/Column';
import DateField from 'components/UI/DateField';
import { PageIndex } from 'components/UI/PageIndex';
import { TableLoader } from 'components/Loaders/TableLoader';
import { getProduct } from './productForm';
import { ProductSerialNumbersMenu } from './ProductSerialNumbersMenu';

interface SerialNumberConfiguration {
  open: boolean;
  product: {
    id: string;
    name: string;
  };
  serial: string;
}

const ProductSerialNumbers = () => {
  const { t } = useTranslation();
  const product = useSelector(getProduct);
  const dispatch = useDispatch();
  const { productId } = useParams<{ productId: string }>();
  const sites = useSelector(getSites);
  const sitesLoaders = useSelector(getSitesLoaders);
  const { isLoading, isSaving } = useSelector(getSerialNumbersLoaders);
  const { loadingError, savingError } = useSelector(getSerialNumbersApiErrors);
  const serialNumbers = useSelector(getSerialNumbers);
  const serialNumberGroups = useSelector(getSerialNumberGroups);
  const pagination = useSelector(getSerialNumbersPagination);
  const selectedSerialNumbers = useSelector(getSelectedSerialNumbers);
  const [currSite, setCurrSite] = useState<any>({ id: '' });
  const [configureLabelForPrintIsOpen, setConfigureLabelForPrintIsOpen] = useState<any>(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [serialNumberConfiguration, setSerialNumberConfiguration] = useState<SerialNumberConfiguration>({
    open: false,
    product: { id: '', name: '' },
    serial: ''
  });
  const [issueSerialsIsOpen, setIssueSerialsIsOpen] = useState<boolean>(false);
  const [confirmDeleteSerialNumber, setConfirmDeleteSerialNumber] = useState<any>({ open: false, target: '' });
  const [issuedAfter, setIssuedAfter] = useState<any>();
  const [issuedBefore, setIssuedBefore] = useState<any>();
  const tableRef = useRef<HTMLTableElement>(null);

  const targets = useMemo(() => Object.keys(selectedSerialNumbers), [selectedSerialNumbers]);

  useEffect(() => {
    if (currSite.id) {
      dispatch(resetSerialNumbersList());
      setCurrSite(currSite);
      dispatch(
        fetchSerialNumbers({
          productId,
          siteId: currSite.id,
          queryIndex: 1,
          querySize: 50,
          issuedAfter,
          issuedBefore
        })
      );
    }
  }, [productId, currSite, issuedAfter, issuedBefore, dispatch]);

  useEffect(() => {
    if (isEmpty(sites)) {
      return;
    }
    setCurrSite(sites[Object.keys(sites)[0]]);
  }, [sites]);

  useEffect(() => {
    return () => {
      dispatch(resetSelectedSerialNumber());
    };
  }, []);

  if (loadingError) {
    return <Alert severity="error">{loadingError}</Alert>;
  }

  return (
    <div>
      <Row alignItems="center">
        <FormControl variant="outlined" fullWidth style={{ position: 'relative' }}>
          <InputLabel id="production-site-label">{t('sites.production')}</InputLabel>
          <Select
            value={currSite.id}
            labelId="production-site-label"
            onChange={(e: ChangeEvent<{ value: any }>) => {
              const {
                target: { value }
              } = e;

              setCurrSite(sites[value]);
            }}
            input={<OutlinedInput label={t('sites.production')} />}
            disabled={isLoading || Object.keys(sites).length === 0}
          >
            {map(sites, (site: any) => (
              <MenuItem key={site.id} value={site.id}>
                {site.name} ({site.code})
              </MenuItem>
            ))}
          </Select>
          {sitesLoaders.isLoading && (
            <CircularProgress
              color="primary"
              size={34}
              style={{
                position: 'absolute',
                right: '3px',
                top: '50%',
                marginTop: -17,
                marginLeft: -17
              }}
            />
          )}
        </FormControl>
        <MuiPickersUtilsProvider libInstance={moment} utils={MomentUtils} locale={moment.locale()}>
          <Column padding="10px 10px 10px 10px" />
          <DateField
            label={t('products.issued after')}
            value={!issuedAfter ? undefined : new Date(issuedAfter)}
            onChange={date => setIssuedAfter(date ? date.toISOString().split('T')[0] : '')}
            disabled={sitesLoaders.isLoading || isLoading}
          />
          <Column padding="10px 10px 10px 10px" />
          <DateField
            label={t('products.issued before')}
            value={!issuedBefore ? undefined : new Date(issuedBefore)}
            onChange={date => setIssuedBefore(date ? date.toISOString().split('T')[0] : '')}
            disabled={sitesLoaders.isLoading || isLoading}
          />
        </MuiPickersUtilsProvider>

        <IconButton
          aria-controls="serial-numbers-menu"
          aria-haspopup="true"
          disabled={isSaving}
          onClick={e => setMenuAnchor(e.currentTarget)}
        >
          {isSaving && (
            <CircularProgress
              color="primary"
              size={48}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: -24,
                marginLeft: -24
              }}
            />
          )}
          <MoreVert />
        </IconButton>

        <ProductSerialNumbersMenu
          serialsCount={serialNumbers.length}
          anchor={menuAnchor}
          close={() => setMenuAnchor(null)}
          issueSerialNumbers={() => setIssueSerialsIsOpen(true)}
          print={() => setConfigureLabelForPrintIsOpen(true)}
        />
      </Row>

      {savingError && (
        <Alert severity="error" style={{ marginTop: '20px' }}>
          {loadingError || savingError}
        </Alert>
      )}

      {isLoading && isEmpty(serialNumberGroups) && <GroupsLoader />}
      {map(serialNumberGroups, (group: SerialNumberGroup, _: number) => (
        <React.Fragment key={group.day}>
          <Typography variant="h6" style={{ margin: '20px 0' }}>
            {moment(group.day).format('ll')}
          </Typography>
          <TableContainer component={Paper} elevation={3} style={{ height: '55vh' }} ref={tableRef}>
            <Table aria-label="order list">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: '5%' }}>
                    <Checkbox
                      checked={group.serial_numbers.every(({ value }) => selectedSerialNumbers[value])}
                      onChange={(__, newValue) => {
                        group.serial_numbers.map(({ value }) => {
                          dispatch(selectSerialNumber({ key: value, value: newValue }));
                        });
                      }}
                      disabled={isLoading}
                    />
                  </TableCell>
                  <TableCell align="left" style={{ width: '25%' }}>
                    {t('label.serial number')}
                  </TableCell>
                  <TableCell align="center" style={{ width: '20%' }}>
                    {t('label.issue date')}
                  </TableCell>
                  <TableCell align="center" style={{ width: '40%' }}>
                    {t('products.third party serial number')}
                  </TableCell>
                  <TableCell align="center" style={{ width: '15%' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && <TableLoader colSpan={5} />}
                {!isLoading && (
                  <>
                    {map(group.serial_numbers, (serialNumber, j) => (
                      <SerialNumberRow
                        key={serialNumber.value}
                        disabled={isSaving || false}
                        serialNumber={serialNumber}
                        showConfiguration={(productForConfig, serial) =>
                          setSerialNumberConfiguration({ open: true, product: productForConfig, serial })
                        }
                        askConfirmDeleteSerialNumber={serialNumberForConfig =>
                          setConfirmDeleteSerialNumber({ open: true, target: serialNumberForConfig })
                        }
                      />
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </React.Fragment>
      ))}
      <PageIndex
        margin="25px 0"
        justifyContent="center"
        index={pagination.index}
        indexesCount={pagination.indexesCount || 0}
        onChange={async (_, page: number) => {
          await dispatch(
            fetchSerialNumbers({
              productId,
              siteId: currSite.id,
              queryIndex: page,
              querySize: 50,
              issuedAfter,
              issuedBefore
            })
          );
          if (tableRef) {
            tableRef?.current?.scrollIntoView();
          }
        }}
      />

      <ConfigureLabelForPrint
        closeFn={() => setConfigureLabelForPrintIsOpen(false)}
        completeFn={() => dispatch(resetSelectedSerialNumber())}
        isOpen={configureLabelForPrintIsOpen}
        properties={product?.properties ?? []}
        targets={targets}
      />

      <SerialNumberSnapshots
        open={serialNumberConfiguration.open}
        serial={serialNumberConfiguration.serial}
        product={serialNumberConfiguration.product}
        closeFn={() => setSerialNumberConfiguration({ open: false, product: { id: '', name: '' }, serial: '' })}
      />

      <IssueSerialNumberCountDialog
        open={issueSerialsIsOpen}
        closeFn={() => setIssueSerialsIsOpen(false)}
        confirmFn={async (count: number, quantity: number) => {
          setIssueSerialsIsOpen(false);
          await dispatch(createSerialNumber({ productId, siteId: currSite.id, count, quantity_per_serial: quantity }));
          dispatch(
            fetchSerialNumbers({
              productId,
              siteId: currSite.id,
              queryIndex: 1,
              querySize: 50,
              issuedAfter,
              issuedBefore
            })
          );
        }}
      />

      <ConfirmDeleteSerialNumber
        closeFn={() => setConfirmDeleteSerialNumber({ open: false, target: '' })}
        target={confirmDeleteSerialNumber.target}
        open={confirmDeleteSerialNumber.open}
        confirmFn={target => {
          setConfirmDeleteSerialNumber({ open: false, target: '' });
          dispatch(deleteSerialNumber(target));
        }}
      />
    </div>
  );
};

export { ProductSerialNumbers };
